import { ENDPOINTS } from '@shared/constants';

import { EndpointParams } from './modules/types';

const BASE_URLS = {
  ASSETS: {
    IMAGES: `/assets/images`,
  },
  AUTH: `/auth`,
  CONNECTED_APPS: `/connected-apps`,
  CONNECTIONS: `/connections`,
  COVER_NOTES: `/cover-notes`,
  CLAIMS: `/claims`,
  DOCUMENTS: '/documents',
  EMAIL_SUGGESTIONS: '/email-suggestions',
  IDENTITIES: `/identities`,
  INTEGRATIONS: {
    ENDPOINTS: `/integrations/endpoints`,
    MAPPING: `/integrations/mappings`,
  },
  PRODUCTS: `/products`,
  POLICIES: `/policies`,
  POLICIES_MEMBER_LIST: `/policies-member-list`,
  INVOICES: `/invoices`,
  INVOICE_ISIB: `/invoices-isib`,
  COMMISSION_CODE: '/commission-codes',
  INVOICE_BULK: '/tempary-invoice-bulk',
  CUSTOMERS_BULK: '/customer-tempory-bulk',
  RECEIPTS: `/receipts`,
  JOB_QUEUE: `/job-queue`,
  MODULES: `/modules`,
  PERSONALIZATION: `/personalization`,
  NOTIFICATION: `/notifications`,
  CONFIGURABLE_FIELDS: {
    VALIDATIONS: `/configurable-fields/validations`,
  },
  WORKFLOWS: `/workflows`,
  WORKFLOW_PHASES: `/workflow-phases`,
  VIRTUAL_MODULES: {
    MODULE_INFO: `/virtual-modules/module-info`,
  },
  REFERENCES: {
    REFERENCE_CATEGORIES: `/reference-categories`,
    REFERENCES: `/references`,
    PUBLIC_REFERENCES: `/reference-categories/public`,
    IAM_REFERENCE_CATEGORIES: `/iam/reference-categories`,
    IAM_REFERENCES: `/iam/references`,
    EXTERNAL_REFERENCE_CATEGORIES: `/external/reference-categories`,
    EXTERNAL_REFERENCES: `/external/references`,
    CLIENT_REFERENCE_CATEGORIES: `/client/reference-categories`,
    CLIENT_REFERENCES: `/client/references`,
    TEMPLATES: `/templates`,
    FORMULA: `/formula`,
    PUBLIC_CATEGORIES_PUBLIC_SINGLE: `/public-refs`,
  },
  SETUP: {
    AUTHORIZERS: `/setup/iam-config/authorizers`,
    IAM_HANDSHAKE: `/setup/iam-config/handshake`,
    BACKUPS: `/setup/backups`,
    BACKUP_SCHEDULES: `/setup/backup-schedules`,
    LANGUAGES: `/setup/languages`,
    REFERENCE_MAPPINGS: `/setup/reference-mappings`,
    CURRENCIES: `/setup/currencies`,
    EXTERNAL_SYSTEMS: `/setup/external-systems`,
    EXTERNAL_SYSTEMS_KEYS: `/setup/external-systems/keys`,
    GATEWAYS: `/setup/payment`,
    CHECKOUT: `/setup/checkout`,
    NOTIFICATION_CONFIG: `/setup/notification-config`,
    EXTERNAL_STORAGE_CONFIG: `/setup/storage/external-storages`,
  },

  PUBLIC_TRANSACTIONS: `/transactions`,
  TRANSACTIONS: `/transaction`,
  STORAGE: {
    INTERNAL: '/internal/storage',
    INTERNAL_ENTITY: '/internal/storage/entity',
    INTERNAL_SHARE_INTERNALLY: '/internal/storage/share/internal',
  },
  SAAS: {
    TENANT: '/saas/tenant',
    SUBSCRIPTIONS: '/saas/subscription',
    PAYMENT: '/saas/payment',
    INVOICES: '/saas/invoice',
  },
  SAAS_SETUP: {
    ADMIN_CONFIG: '/setup/saas/admin-config',
  },

  LEADS: `/leads`,
  QUOTATIONS: `/leads/quotations`,
  /********************* BMS *********************/
  INSURERS: `/insurers`,
  COMMISSIONS: `/commissions`,
  EXTERNAL_USERS: `/external-users`,
  DEBIT_NOTES: '/debit-notes',
  ADDITIONAL_STORAGE: '/additional-storage',
  CREDIT_DEBIT_NOTES: '/credit-debit-notes',
  REPORTS: '/reports',
  CUSTOMERS: '/customers',
  ENDORSEMENTS: `/endorsements`,
  RECONCILE: `/reconciliation`,
  AGENT_COMMISSION_CODES: `/agent-commission-codes`,
  ADMIN_CONFIG: '/setup/saas/admin-config',
  SUBSCRIBERS: `/subscribers`,
  TICKETS: `/tickets`,
  LEGIFY_AUTH: `/legify-auth`,

  AI_SECTION: `/section`,
  AI_COLLAB: `/collab`,
};

/**
 *
 * NOTE: if you get an error here that might be due to missing cases.
 * Check whether you have added all the ENDPOINTS to cases in below switch or not
 *
 */
const getURLPath = (endpoint: EndpointParams): string => {
  switch (endpoint.endpoint) {
    // ------------------------------------------------------ GENERAL ENDPOINTS ------------------------------------------------------ //

    // AUTH
    case ENDPOINTS.AUTH_REFRESH_TOKEN:
      return `${BASE_URLS.AUTH}/token`;

    case ENDPOINTS.AUTH_LOGIN:
      return `${BASE_URLS.AUTH}/login`;

    case ENDPOINTS.AUTH_PROFILE_IMAGE_UPLOAD:
      return `${BASE_URLS.AUTH}/upload/profile-image`;

    case ENDPOINTS.AUTH_LOGIN_IMPERSONATE:
      return `${BASE_URLS.AUTH}/impersonate/login`;

    case ENDPOINTS.AUTH_CHECK_USER:
      return `${BASE_URLS.AUTH}/check`;

    case ENDPOINTS.AUTH_LOGOUT:
      return `${BASE_URLS.AUTH}/logout`;

    case ENDPOINTS.AUTH_FORGOT_PASSWORD:
      return `${BASE_URLS.AUTH}/password/forgot`;

    case ENDPOINTS.AUTH_VALIDATE_TOKEN:
      return `${BASE_URLS.AUTH}/token/validate`;

    case ENDPOINTS.AUTH_RESET_PASSWORD:
      return `${BASE_URLS.AUTH}/password/reset`;

    case ENDPOINTS.AUTH_REGISTER:
      return `${BASE_URLS.AUTH}/register`;

    case ENDPOINTS.AUTH_VERIFY:
      return `${BASE_URLS.AUTH}/verify`;

    case ENDPOINTS.AUTH_PROFILE:
      return `${BASE_URLS.AUTH}/profile`;

    case ENDPOINTS.AUTH_SEND_OTP:
      return `${BASE_URLS.AUTH}/otp/send`;

    case ENDPOINTS.AUTH_USERNAME:
      return `${BASE_URLS.AUTH}/username`;

    case ENDPOINTS.AUTH_CODE:
      return `${BASE_URLS.AUTH}/code`;

    // CONNECTED - APPS
    case ENDPOINTS.CONNECTED_APPS_CONNECT_NEW_APPS:
    case ENDPOINTS.CONNECTED_APPS_GET_ALL_APPS:
      return BASE_URLS.CONNECTED_APPS;

    case ENDPOINTS.CONNECTED_APPS_GET_ONE_APP:
    case ENDPOINTS.CONNECTED_APPS_UPDATE_ONE_APP:
    case ENDPOINTS.CONNECTED_APPS_DELETE_ONE_APP:
      return `${BASE_URLS.CONNECTED_APPS}/${endpoint.params.id}`;

    case ENDPOINTS.CONNECTED_APPS_GET_ALL_FIELDS_OF_A_CONNECTED_APP:
      return `${BASE_URLS.CONNECTED_APPS}/${endpoint.params.id}/fields`;

    case ENDPOINTS.CONNECTED_APPS_GET_ALL_ROLES_OF_A_CONNECTED_APP:
      return `${BASE_URLS.CONNECTED_APPS}/${endpoint.params.id}/roles`;

    case ENDPOINTS.CONNECTED_APPS_GET_ALL_PERMISSIONS_OF_A_CONNECTED_APP:
      return `${BASE_URLS.CONNECTED_APPS}/${endpoint.params.id}/permissions`;

    case ENDPOINTS.CONNECTED_APPS_GET_ALL_INHERITED_PERMISSIONS_OF_A_CONNECTED_APP:
      return `${BASE_URLS.CONNECTED_APPS}/${endpoint.params.id}/inherited-permissions`;

    case ENDPOINTS.CONNECTED_APPS_GET_ALL_DOCUMENTATIONS_OF_A_CONNECTED_APP:
      return `${BASE_URLS.CONNECTED_APPS}/${endpoint.params.id}/docs`;

    case ENDPOINTS.CONNECTED_APPS_CONNECT_OR_DISCONNECT_APP:
      return `${BASE_URLS.CONNECTED_APPS}/${endpoint.params.id}/${endpoint.params.status_change}`;

    case ENDPOINTS.CONNECTED_APPS_GET_ALL_REFERENCES_OF_A_CONNECTED_APP:
      return `${BASE_URLS.CONNECTED_APPS}/${endpoint.params.id}/reference-categories/${endpoint.params.categoryId}/references`;

    case ENDPOINTS.CONNECTED_APPS_HANDSHAKE:
      return `${BASE_URLS.CONNECTED_APPS}/handshake`;

    case ENDPOINTS.CONNECTED_APPS_GENERATE_SECRETS:
      return `${BASE_URLS.CONNECTED_APPS}/generate-secrets`;

    case ENDPOINTS.CONNECTED_APPS_GENERATE_PUBLIC_API_KEY:
      return `${BASE_URLS.CONNECTED_APPS}/${endpoint.params.id}/keys/generate`;

    case ENDPOINTS.CONNECTED_APPS_GENERATE_PUBLIC_CLIENT_CREDENTIALS:
      return `${BASE_URLS.CONNECTED_APPS}/${endpoint.params.id}/keys/generate-client-credentials`;

    case ENDPOINTS.CONNECTED_APPS_SAVE_DELETED_CLIENT_SECRET:
      return `${BASE_URLS.CONNECTED_APPS}/${endpoint.params.id}/keys/save-deleted-client-secret`;

    case ENDPOINTS.CONNECTED_APPS_SAVE_DELETED_PUBLIC_KEY:
      return `${BASE_URLS.CONNECTED_APPS}/${endpoint.params.id}/keys/save-deleted-api-key`;

    case ENDPOINTS.CONNECTED_APPS_SAVE_GENERATED_PUBLIC_KEY:
      return `${BASE_URLS.CONNECTED_APPS}/${endpoint.params.id}/keys`;

    case ENDPOINTS.CONNECTED_APPS_UPDATE_GENERATED_PUBLIC_KEY:
      return `${BASE_URLS.CONNECTED_APPS}/${endpoint.params.appId}/keys/${endpoint.params.id}`;

    case ENDPOINTS.CONNECTED_APPS_GET_SPECIFIC_GENERATED_KEY:
      return `${BASE_URLS.CONNECTED_APPS}/${endpoint.params.appId}/keys/${endpoint.params.id}`;

    case ENDPOINTS.CONNECTED_APPS_GET_ALL_GENERATED_KEYS:
      return `${BASE_URLS.CONNECTED_APPS}/${endpoint.params.id}/keys`;

    case ENDPOINTS.CONNECTED_APPS_DELETE_KEY:
      return `${BASE_URLS.CONNECTED_APPS}/${endpoint.params.appId}/keys/${endpoint.params.id}`;

    case ENDPOINTS.CONNECTED_APPS_RESTORE_KEY:
      return `${BASE_URLS.CONNECTED_APPS}/${endpoint.params.appId}/keys/${endpoint.params.id}/restore`;

    //CONNECTIONS
    case ENDPOINTS.CONNECTIONS_GET_ALL:
      return BASE_URLS.CONNECTIONS;

    case ENDPOINTS.CONNECTIONS_UPDATE:
      return `${BASE_URLS.CONNECTIONS}/${endpoint.params.id}`;

    // COVER-NOTES
    case ENDPOINTS.CREATE_NEW_COVER_NOTE:
    case ENDPOINTS.COVER_NOTES_GET_ALL:
      return BASE_URLS.COVER_NOTES;
    case ENDPOINTS.ISSUE_COVER_NOTE:
      return `${BASE_URLS.COVER_NOTES}/${endpoint.params.id}/issue`;
    case ENDPOINTS.CANCEL_COVER_NOTE:
      return `${BASE_URLS.COVER_NOTES}/${endpoint.params.id}/cancel`;
    case ENDPOINTS.COVER_NOTE_SHARE_TO_CUSTOMER:
      return `${BASE_URLS.COVER_NOTES}/share-document`;

    // ENDORSEMENTS
    case ENDPOINTS.ENDORSEMENTS_GET_ALL:
      return BASE_URLS.ENDORSEMENTS;

    case ENDPOINTS.ENDORSEMENTS_ADD_ONE:
      return BASE_URLS.ENDORSEMENTS;

    case ENDPOINTS.ENDORSEMENTS_UPDATE_ONE:
      return `${BASE_URLS.ENDORSEMENTS}/${endpoint.params.id}`;

    case ENDPOINTS.ENDORSEMENTS_GET_PENDING_COUNT:
      return `${BASE_URLS.ENDORSEMENTS}/pending-count/${endpoint.params.id}`;

    case ENDPOINTS.ENDORSEMENTS_SEND_EMAIL:
      return `${BASE_URLS.ENDORSEMENTS}/send-endorsement-ccm/${endpoint.params.id}`;
    //DOCUMENTS
    case ENDPOINTS.VARIABLES_ADD_ONE:
    case ENDPOINTS.VARIABLES_GET_ALL:
      return `${BASE_URLS.DOCUMENTS}/${endpoint.params.id}/variables`;
    case ENDPOINTS.VARIABLES_DELETE_ONE:
      return `${BASE_URLS.DOCUMENTS}/${endpoint.params.id}/del-variable`;

    // EMAIL SUGGESTIONS
    case ENDPOINTS.EMAIL_SUGGESTIONS_SEARCH:
    case ENDPOINTS.EMAIL_SUGGESTIONS_SAVE:
    case ENDPOINTS.EMAIL_SUGGESTIONS_DELETE:
      return `${BASE_URLS.EMAIL_SUGGESTIONS}`;

    // IDENTITIES
    case ENDPOINTS.IDENTITIES_SUBORDINATES:
      return `${BASE_URLS.IDENTITIES}/subordinates`;

    case ENDPOINTS.IDENTITIES_SUBORDINATE_IDS:
      return `${BASE_URLS.IDENTITIES}/subordinate-ids`;

    case ENDPOINTS.IDENTITIES_HIERARCHICAL:
      return `${BASE_URLS.IDENTITIES}/hierarchy`;

    case ENDPOINTS.IDENTITIES_GET_INTERNAL_FIELD:
      return `${BASE_URLS.IDENTITIES}/get-field-data`;

    case ENDPOINTS.IDENTITIES_GET_ONE:
      return `${BASE_URLS.IDENTITIES}/${endpoint.params.id}`;

    // INTEGRATION - ENDPOINTS

    case ENDPOINTS.INTEGRATION_ENDPOINTS_GET_ALL:
    case ENDPOINTS.INTEGRATION_ENDPOINTS_ADD_ONE:
      return BASE_URLS.INTEGRATIONS.ENDPOINTS;
    case ENDPOINTS.INTEGRATION_ENDPOINTS_UPDATE_ONE:
    case ENDPOINTS.INTEGRATION_ENDPOINTS_DELETE_ONE:
      return `${BASE_URLS.INTEGRATIONS.ENDPOINTS}/${endpoint.params.id}`;
    case ENDPOINTS.INTEGRATION_ENDPOINTS_RESTORE_ONE:
      return `${BASE_URLS.INTEGRATIONS.ENDPOINTS}/${endpoint.params.id}/restore`;
    case ENDPOINTS.INTEGRATION_ENDPOINTS_VERIFY_ONE:
      return `${BASE_URLS.INTEGRATIONS.ENDPOINTS}/${endpoint.params.id}/verify`;

    // INTEGRATION -MAPPINGS
    case ENDPOINTS.INTEGRATION_MAPPINGS_ADD_ONE:
    case ENDPOINTS.INTEGRATION_MAPPINGS_GET_ALL:
      return BASE_URLS.INTEGRATIONS.MAPPING;
    case ENDPOINTS.INTEGRATION_MAPPINGS_UPDATE_ONE:
    case ENDPOINTS.INTEGRATION_MAPPINGS_DELETE_ONE:
      return `${BASE_URLS.INTEGRATIONS.MAPPING}/${endpoint.params.id}`;
    case ENDPOINTS.INTEGRATION_MAPPINGS_RESTORE_ONE:
      return `${BASE_URLS.INTEGRATIONS.MAPPING}/${endpoint.params.id}/restore`;
    case ENDPOINTS.INTEGRATION_MAPPINGS_VERIFY_ONE:
      return `${BASE_URLS.INTEGRATIONS.MAPPING}/${endpoint.params.id}/verify`;

    // VIRTUAL_MODULE - WORKFLOWS
    case ENDPOINTS.VIRTUAL_MODULES_ADD_ONE_WORKFLOW:
    case ENDPOINTS.VIRTUAL_MODULES_GET_ALL_WORKFLOWS:
      return BASE_URLS.WORKFLOWS;
    case ENDPOINTS.VIRTUAL_MODULES_ADD_ONE_WORKFLOW_PHASE:
      return BASE_URLS.WORKFLOW_PHASES;
    case ENDPOINTS.VIRTUAL_MODULES_GET_ALL_WORKFLOW_PHASES:
      return `${BASE_URLS.WORKFLOW_PHASES}/workflow/${endpoint.params.workflowId}`;
    case ENDPOINTS.VIRTUAL_MODULES_GET_ONE_WORKFLOW:
    case ENDPOINTS.VIRTUAL_MODULES_WORKFLOWS_UPDATE_ONE:
    case ENDPOINTS.VIRTUAL_MODULES_WORKFLOWS_DELETE_ONE:
      return `${BASE_URLS.WORKFLOWS}/${endpoint.params.id}`;

    // VIRTUAL_MODULE - MODULE MODULE INFORMATION

    case ENDPOINTS.VIRTUAL_MODULES_MODULE_INFO_GET_ALL:
    case ENDPOINTS.VIRTUAL_MODULES_MODULE_INFO_ADD_ONE:
      return BASE_URLS.VIRTUAL_MODULES.MODULE_INFO;
    case ENDPOINTS.VIRTUAL_MODULES_MODULE_INFO_UPDATE_ONE:
    case ENDPOINTS.VIRTUAL_MODULES_MODULE_INFO_GET_ONE:
    case ENDPOINTS.VIRTUAL_MODULES_MODULE_INFO_DELETE_ONE:
      return `${BASE_URLS.VIRTUAL_MODULES.MODULE_INFO}/${endpoint.params.id}`;
    case ENDPOINTS.VIRTUAL_MODULES_MODULE_INFO_RESTORE_ONE:
      return `${BASE_URLS.VIRTUAL_MODULES.MODULE_INFO}/${endpoint.params.id}/restore`;
    case ENDPOINTS.VIRTUAL_MODULES_MODULE_INFO_VERIFY_ONE:
      return `${BASE_URLS.VIRTUAL_MODULES.MODULE_INFO}/${endpoint.params.id}/verify`;
    case ENDPOINTS.VIRTUAL_MODULES_MODULE_INFO_GET_RESPONSE:
      return `${BASE_URLS.VIRTUAL_MODULES.MODULE_INFO}/collection-info/${endpoint.params.id}/response`;
    case ENDPOINTS.VIRTUAL_MODULES_MODULE_INFO_GET_BY_STATUS:
      return `${BASE_URLS.VIRTUAL_MODULES.MODULE_INFO}/${endpoint.params.id}/status`;

    // JOB_QUEUE
    case ENDPOINTS.JOB_QUEUE_CHECK_STATUS:
      return `${BASE_URLS.JOB_QUEUE}/${endpoint.params.id}`;

    // MODULES
    case ENDPOINTS.MODULES_GET_ALL:
      return BASE_URLS.MODULES;

    case ENDPOINTS.MODULES_GET_ONE_BY_ID:
    case ENDPOINTS.MODULES_UPDATE:
      return `${BASE_URLS.MODULES}/${endpoint.params.id}`;

    case ENDPOINTS.MODULES_RESET:
      return `${BASE_URLS.MODULES}/${endpoint.params.id}/reset`;

    // PERSONALIZATION
    case ENDPOINTS.PERSONALIZATION_GET:
    case ENDPOINTS.PERSONALIZATION_UPDATE:
      return BASE_URLS.PERSONALIZATION;

    // NOTIFICATION
    case ENDPOINTS.NOTIFICATION_CERATE_WITHOUT_USER:
      return BASE_URLS.NOTIFICATION;

    case ENDPOINTS.NOTIFICATION_CREATE_MULTIPLE_NOTIFICATIONS_WITH_USERS:
      return `${BASE_URLS.NOTIFICATION}/${endpoint.params.id}/user-notifications`;

    case ENDPOINTS.NOTIFICATION_CERATE_WITH_USER:
      return `${BASE_URLS.NOTIFICATION}/user-notifications`;

    case ENDPOINTS.NOTIFICATION_GET:
      return `${BASE_URLS.NOTIFICATION}/${endpoint.params.id}`;

    case ENDPOINTS.NOTIFICATION_GET_USER_NOTIFICATIONS:
      return `${BASE_URLS.NOTIFICATION}/${endpoint.params.id}/user-notifications`;

    case ENDPOINTS.NOTIFICATION_FILTER:
      return BASE_URLS.NOTIFICATION;

    case ENDPOINTS.NOTIFICATIONS_MARK_AS_READ:
      return `${BASE_URLS.NOTIFICATION}/${endpoint.params.id}/read`;

    case ENDPOINTS.NOTIFICATIONS_MARK_ALL_AS_READ:
      return `${BASE_URLS.NOTIFICATION}/mark-all-read`;

    case ENDPOINTS.NOTIFICATION_CONFIG_EMAIL:
      return `${BASE_URLS.SETUP.NOTIFICATION_CONFIG}/${endpoint.params.tenantId}`;

    // CONFIGURABLE_FIELDS - VALIDATIONS
    case ENDPOINTS.CONFIGURABLE_FIELDS_VALIDATIONS_NAME:
      return `${BASE_URLS.CONFIGURABLE_FIELDS.VALIDATIONS}/name`;

    case ENDPOINTS.CONFIGURABLE_FIELDS_VALIDATIONS_PHONE_NUMBER:
      return `${BASE_URLS.CONFIGURABLE_FIELDS.VALIDATIONS}/phone-number`;

    case ENDPOINTS.CONFIGURABLE_FIELDS_VALIDATIONS_ADDRESS:
      return `${BASE_URLS.CONFIGURABLE_FIELDS.VALIDATIONS}/address`;

    case ENDPOINTS.CONFIGURABLE_FIELDS_VALIDATIONS_EMAIL:
      return `${BASE_URLS.CONFIGURABLE_FIELDS.VALIDATIONS}/email`;

    // REFERENCES - PREDEFINED_REFERENCES
    case ENDPOINTS.PREDEFINED_REFERENCES_GET_VALUE:
      return `${BASE_URLS.REFERENCES.REFERENCES}/predefined-value`;

    case ENDPOINTS.PREDEFINED_REFERENCES_GET_ALL_CITIES:
      return `${BASE_URLS.REFERENCES.REFERENCE_CATEGORIES}/predefined/cities/all`;

    case ENDPOINTS.PREDEFINED_REFERENCES_SEARCH_CITIES:
      return `${BASE_URLS.REFERENCES.REFERENCE_CATEGORIES}/predefined/cities/search`;

    // REFERENCES - REFERENCE_CATEGORIES
    case ENDPOINTS.REFERENCE_CATEGORIES_GET_ALL:
    case ENDPOINTS.REFERENCE_CATEGORIES_ADD_NEW:
      return BASE_URLS.REFERENCES.REFERENCE_CATEGORIES;

    case ENDPOINTS.REFERENCE_CATEGORIES_GET_SINGLE:
      return `${BASE_URLS.REFERENCES.REFERENCE_CATEGORIES}/${endpoint.params.id}`;

    case ENDPOINTS.REFERENCE_CATEGORIES_GET_EXTERNAL:
      return `${BASE_URLS.REFERENCES.REFERENCE_CATEGORIES}/external/${endpoint.params.id}`;

    case ENDPOINTS.REFERENCE_CATEGORIES_GET_EXTERNAL_DELETE:
      return `${BASE_URLS.REFERENCES.REFERENCE_CATEGORIES}/external/delete/${endpoint.params.id}`;

    case ENDPOINTS.REFERENCE_CATEGORIES_UPDATE:
      return `${BASE_URLS.REFERENCES.REFERENCE_CATEGORIES}/${endpoint.params.id}`;

    case ENDPOINTS.REFERENCE_CATEGORIES_DELETE:
      return `${BASE_URLS.REFERENCES.REFERENCE_CATEGORIES}/${endpoint.params.id}/delete`;

    case ENDPOINTS.REFERENCE_CATEGORIES_RESTORE:
      return `${BASE_URLS.REFERENCES.REFERENCE_CATEGORIES}/${endpoint.params.id}/restore`;

    case ENDPOINTS.REFERENCE_CATEGORIES_REFERENCES_GET_ALL:
    case ENDPOINTS.REFERENCE_CATEGORIES_REFERENCES_ADD_NEW:
      return `${BASE_URLS.REFERENCES.REFERENCE_CATEGORIES}/${endpoint.params.id}/references`;

    case ENDPOINTS.REFERENCE_CATEGORIES_REFERENCES_GET_ALL_V2:
      return `/v2${BASE_URLS.REFERENCES.REFERENCE_CATEGORIES}/${endpoint.params.id}/references`;
    // REFERENCES - REFERENCES
    case ENDPOINTS.REFERENCE_EDIT:
      return `${BASE_URLS.REFERENCES.REFERENCE_CATEGORIES}/${endpoint.params.categoryId}/references/${endpoint.params.referenceId}`;

    case ENDPOINTS.REFERENCE_DELETE:
      return `${BASE_URLS.REFERENCES.REFERENCE_CATEGORIES}/${endpoint.params.categoryId}/references/${endpoint.params.referenceId}/delete`;

    case ENDPOINTS.REFERENCE_RESTORE:
      return `${BASE_URLS.REFERENCES.REFERENCE_CATEGORIES}/${endpoint.params.categoryId}/references/${endpoint.params.referenceId}/restore`;

    case ENDPOINTS.REFERENCES_FIELDS_GET_VALUE:
      return `${BASE_URLS.REFERENCES.REFERENCES}/${endpoint.params.id}/fields/${endpoint.params.field_id}/value`;

    // REFERENCES - BULK ADD
    case ENDPOINTS.REFERENCE_CATEGORIES_REFERENCES_BULK_ADD:
      return `${BASE_URLS.REFERENCES.REFERENCE_CATEGORIES}/${endpoint.params.id}/references/bulk`;

    // SUBSCRIBERS
    case ENDPOINTS.GET_ALL_SUBSCRIBER:
    case ENDPOINTS.ADD_SUBSCRIBER:
      return BASE_URLS.SUBSCRIBERS;

    case ENDPOINTS.UPDATE_ONE_SUBSCRIBER:
    case ENDPOINTS.DELETE_ONE_SUBSCRIBER:
      return `${BASE_URLS.SUBSCRIBERS}/${endpoint.params.id}`;

    case ENDPOINTS.RESTORE_SUBSCRIBER:
      return `${BASE_URLS.SUBSCRIBERS}/${endpoint.params.id}/restore`;

    // REFERENCES - TEMPLATES
    case ENDPOINTS.TEMPLATES_ADD_NEW:
      return BASE_URLS.REFERENCES.TEMPLATES;

    case ENDPOINTS.TEMPLATES_UPDATE_ONE:
    case ENDPOINTS.TEMPLATES_GET_ONE:
      return `${BASE_URLS.REFERENCES.TEMPLATES}/${endpoint.params.id}`;

    // REFERENCES - FORMULA
    case ENDPOINTS.FORMULA_ANALYZE:
      return `${BASE_URLS.REFERENCES.FORMULA}/analyze`;

    case ENDPOINTS.FORMULA_EVALUATE:
      return `${BASE_URLS.REFERENCES.FORMULA}/evaluate`;

    // STORAGE - INTERNAL
    case ENDPOINTS.STORAGE_INTERNAL_UPLOAD:
      return `${BASE_URLS.STORAGE.INTERNAL}/upload`;

    case ENDPOINTS.STORAGE_INTERNAL_DELETE:
      return `${BASE_URLS.STORAGE.INTERNAL_ENTITY}/${endpoint.params.entityId}`;

    case ENDPOINTS.STORAGE_INTERNAL_PREVIEW:
      return `${BASE_URLS.STORAGE.INTERNAL_ENTITY}/${endpoint.params.entityId}/preview`;

    case ENDPOINTS.STORAGE_INTERNAL_DOWNLOAD:
      return `${BASE_URLS.STORAGE.INTERNAL_ENTITY}/${endpoint.params.entityId}/download`;

    case ENDPOINTS.STORAGE_INTERNAL_SHARE_INTERNALLY:
      return `${BASE_URLS.STORAGE.INTERNAL_ENTITY}/${endpoint.params.entityId}/share/internal`;

    case ENDPOINTS.STORAGE_INTERNAL_CONTENT:
      return `${BASE_URLS.STORAGE.INTERNAL_ENTITY}/${endpoint.params.entityId}/content`;

    // SETUP - REFERENCE_MAPPING
    case ENDPOINTS.SETUP_GET_ALL_REFERENCE_MAPPINGS:
      return BASE_URLS.SETUP.REFERENCE_MAPPINGS;

    case ENDPOINTS.SETUP_GET_REFERENCE_MAPPING_BY_KEY:
      return `${BASE_URLS.SETUP.REFERENCE_MAPPINGS}/${endpoint.params.key}`;

    case ENDPOINTS.SETUP_UPDATE_REFERENCE_MAPPING:
      return `${BASE_URLS.SETUP.REFERENCE_MAPPINGS}/${endpoint.params.id}`;

    // REFERENCES - PUBLIC
    case ENDPOINTS.REFERENCE_GET_PUBLIC_REFERENCES:
      return `${BASE_URLS.REFERENCES.PUBLIC_REFERENCES}/${endpoint.params.id}/references`;
    case ENDPOINTS.REFERENCE_COUNTRIES_PUBLIC_GET_SINGLE:
      return `${BASE_URLS.REFERENCES.PUBLIC_CATEGORIES_PUBLIC_SINGLE}/category/country`;
    case ENDPOINTS.REFERENCE_CATEGORIES_PUBLIC_GET_COUNTRIES_REFERENCES:
      return `${BASE_URLS.REFERENCES.PUBLIC_CATEGORIES_PUBLIC_SINGLE}/countries/${endpoint.params.id}/references`;

    // SETUP - BACKUP_SCHEDULES
    case ENDPOINTS.SETUP_BACKUPS_SCHEDULES_ADD_NEW:
      return BASE_URLS.SETUP.BACKUP_SCHEDULES;

    case ENDPOINTS.SETUP_BACKUPS_SCHEDULES_GET_ALL:
      return BASE_URLS.SETUP.BACKUP_SCHEDULES;

    case ENDPOINTS.SETUP_BACKUPS_SCHEDULES_DELETE_ONE:
      return `${BASE_URLS.SETUP.BACKUP_SCHEDULES}/${endpoint.params.id}`;

    case ENDPOINTS.SETUP_BACKUPS_SCHEDULES_ENABLE_DISABLE_ONE:
      return `${BASE_URLS.SETUP.BACKUP_SCHEDULES}/${endpoint.params.id}/${endpoint.params.status}`;

    // SETUP - IAM
    case ENDPOINTS.SETUP_IAM_AUTHORIZERS_AZURE:
      return `${BASE_URLS.SETUP.AUTHORIZERS}/azure`;

    case ENDPOINTS.SETUP_IAM_HANDSHAKE:
      return `${BASE_URLS.SETUP.IAM_HANDSHAKE}`;

    // SETUP - BACKUPS
    case ENDPOINTS.SETUP_BACKUPS_BACKUP_NOW:
    case ENDPOINTS.SETUP_BACKUPS_GET_ALL:
      return BASE_URLS.SETUP.BACKUPS;

    case ENDPOINTS.SETUP_BACKUPS_DOWNLOAD_ONE:
    case ENDPOINTS.SETUP_BACKUPS_DELETE_ONE:
      return `${BASE_URLS.SETUP.BACKUPS}/${endpoint.params.name}`;

    // SETUP - LANGUAGES
    case ENDPOINTS.SETUP_LANGUAGES_ADD_ONE:
    case ENDPOINTS.SETUP_LANGUAGES_GET_ALL:
      return BASE_URLS.SETUP.LANGUAGES;

    case ENDPOINTS.SETUP_LANGUAGES_GET_ONE:
    case ENDPOINTS.SETUP_LANGUAGES_UPDATE_ONE:
    case ENDPOINTS.SETUP_LANGUAGES_ENABLE_DISABLE_ONE:
    case ENDPOINTS.SETUP_LANGUAGES_DELETE_ONE:
      return `${BASE_URLS.SETUP.LANGUAGES}/${endpoint.params.id}`;

    case ENDPOINTS.SETUP_LANGUAGES_REFRESH_ONE:
      return `${BASE_URLS.SETUP.LANGUAGES}/${endpoint.params.id}/refresh`;

    case ENDPOINTS.SETUP_LANGUAGES_GET_CONTENT_OF_ONE:
      return `${BASE_URLS.SETUP.LANGUAGES}/${endpoint.params.id}/content`;

    // SETUP - CURRENCIES
    case ENDPOINTS.SETUP_CURRENCIES_ADD_ONE:
    case ENDPOINTS.SETUP_CURRENCIES_GET_ALL:
      return BASE_URLS.SETUP.CURRENCIES;

    case ENDPOINTS.SETUP_CURRENCIES_GET_ONE:
    case ENDPOINTS.SETUP_CURRENCIES_UPDATE_ONE:
      return `${BASE_URLS.SETUP.CURRENCIES}/${endpoint.params.id}`;

    case ENDPOINTS.SETUP_CURRENCIES_DELETE_ONE:
      return `${BASE_URLS.SETUP.CURRENCIES}/${endpoint.params.id}/delete`;

    case ENDPOINTS.SETUP_CURRENCIES_RESTORE_ONE:
      return `${BASE_URLS.SETUP.CURRENCIES}/${endpoint.params.id}/restore`;

    case ENDPOINTS.SETUP_CURRENCIES_TOGGLE_BASE_CURRENCY:
      return `${BASE_URLS.SETUP.CURRENCIES}/${endpoint.params.id}/base_currency`;

    // SETUP - STORAGE - EXTERNAL STORAGE CONFIG
    case ENDPOINTS.SETUP_STORAGE_EXTERNAL_STORAGE_CONFIG_GET_ALL:
    case ENDPOINTS.SETUP_STORAGE_EXTERNAL_STORAGE_CONFIG_CREATE:
      return BASE_URLS.SETUP.EXTERNAL_STORAGE_CONFIG;
    case ENDPOINTS.SETUP_STORAGE_EXTERNAL_STORAGE_CONFIG_GET_ONE:
    case ENDPOINTS.SETUP_STORAGE_EXTERNAL_STORAGE_CONFIG_UPDATE:
      return `${BASE_URLS.SETUP.EXTERNAL_STORAGE_CONFIG}/${endpoint.params.id}`;
    case ENDPOINTS.SETUP_STORAGE_EXTERNAL_STORAGE_CONFIG_DELETE:
      return `${BASE_URLS.SETUP.EXTERNAL_STORAGE_CONFIG}/${endpoint.params.id}/delete`;

    // ASSETS
    case ENDPOINTS.ASSETS_IMAGES_UPLOAD:
      return BASE_URLS.ASSETS.IMAGES;

    case ENDPOINTS.ASSETS_IMAGES_DELETE:
      return `${BASE_URLS.ASSETS.IMAGES}/${endpoint.params.name}`;

    // SETUP - EXTERNAL SYSTEMS
    case ENDPOINTS.SETUP_EXTERNAL_SYSTEMS_GET_ALL:
      return BASE_URLS.SETUP.EXTERNAL_SYSTEMS;

    case ENDPOINTS.SETUP_EXTERNAL_SYSTEM_GET_EXTERNAL_REFERENCE_CATEGORIES:
      return `${BASE_URLS.SETUP.EXTERNAL_SYSTEMS}/${endpoint.params.id}/reference-categories`;

    case ENDPOINTS.SETUP_EXTERNAL_SYSTEMS_UPDATE_ONE:
      return `${BASE_URLS.SETUP.EXTERNAL_SYSTEMS}/${endpoint.params.id}`;

    case ENDPOINTS.SETUP_EXTERNAL_SYSTEMS_KEYS_SAVE_KEY:
    case ENDPOINTS.SETUP_EXTERNAL_SYSTEMS_KEYS_GET_ALL:
      return BASE_URLS.SETUP.EXTERNAL_SYSTEMS_KEYS;

    case ENDPOINTS.SETUP_EXTERNAL_SYSTEMS_KEYS_GENERATE_NEW_KEY:
      return `${BASE_URLS.SETUP.EXTERNAL_SYSTEMS_KEYS}/generate`;

    case ENDPOINTS.SETUP_EXTERNAL_SYSTEMS_KEYS_GET_ONE:
    case ENDPOINTS.SETUP_EXTERNAL_SYSTEMS_KEYS_DELETE_ONE:
      return `${BASE_URLS.SETUP.EXTERNAL_SYSTEMS_KEYS}/${endpoint.params.id}`;
    case ENDPOINTS.SETUP_EXTERNAL_SYSTEMS_KEYS_RESTORE_ONE:
      return `${BASE_URLS.SETUP.EXTERNAL_SYSTEMS_KEYS}/${endpoint.params.id}/restore`;

    // GATEWAYS
    case ENDPOINTS.GATEWAYS_GET_ALL:
      return `${BASE_URLS.SETUP.GATEWAYS}`;
    case ENDPOINTS.GATEWAYS_GET_ONE:
      return `${BASE_URLS.SETUP.GATEWAYS}/${endpoint.params.id}/gateway`;
    case ENDPOINTS.GATEWAYS_ENABLE:
      return `${BASE_URLS.SETUP.GATEWAYS}/${endpoint.params.id}/enable`;
    case ENDPOINTS.GATEWAYS_DISABLE:
      return `${BASE_URLS.SETUP.GATEWAYS}/${endpoint.params.id}/disable`;
    case ENDPOINTS.GATEWAYS_SANDBOX_ENABLE:
      return `${BASE_URLS.SETUP.GATEWAYS}/${endpoint.params.id}/sandbox/enable`;
    case ENDPOINTS.GATEWAYS_SANDBOX_DISABLE:
      return `${BASE_URLS.SETUP.GATEWAYS}/${endpoint.params.id}/sandbox/disable`;
    case ENDPOINTS.GATEWAY_UPDATE_CONFIG:
      return `${BASE_URLS.SETUP.GATEWAYS}/${endpoint.params.id}/configuration`;

    // CHECKOUTS
    case ENDPOINTS.CHECKOUT_ALL_ALLOWED_PAYMENT_METHODS:
      return `${BASE_URLS.SETUP.CHECKOUT}/payment-methods`;
    case ENDPOINTS.CHECKOUT_GENERATE_HASH:
      return `${BASE_URLS.SETUP.GATEWAYS}/hash`;
    case ENDPOINTS.CHECKOUT_PAYHERE_DETAILS:
      return `${BASE_URLS.SETUP.GATEWAYS}/payhere`;
    case ENDPOINTS.TRANSACTION_CREATE_TRANSACTION:
      return `${BASE_URLS.SETUP.GATEWAYS}/`;
    case ENDPOINTS.TRANSACTION_CREATE_RECEIPT:
      return `${BASE_URLS.SETUP.GATEWAYS}/receipt`;

    // TRANSACTIONS
    case ENDPOINTS.TRANSACTIONS_GET_ALL:
      return `${BASE_URLS.TRANSACTIONS}/`;

    case ENDPOINTS.RECEIPTS_SEND_BODIES_TO_TRANSACTION:
      return `${BASE_URLS.PUBLIC_TRANSACTIONS}/bodies`;

    case ENDPOINTS.INITIALIZE_TRANSACTION_CREATE:
      return `${BASE_URLS.PUBLIC_TRANSACTIONS}/initialize`;
    case ENDPOINTS.GET_TRANSACTION_BY_ORDER_ID:
      return `${BASE_URLS.PUBLIC_TRANSACTIONS}/order/${endpoint.params.orderID}`;
    case ENDPOINTS.TRANSACTION_UPDATE_TRANSACTION_STATUS:
      return `${BASE_URLS.PUBLIC_TRANSACTIONS}/${endpoint.params.id}`;
    case ENDPOINTS.GET_TRANSACTION:
      return `${BASE_URLS.PUBLIC_TRANSACTIONS}/${endpoint.params.transactionId}`;

    // ------------------------------------------------------ GENERAL ENDPOINTS ------------------------------------------------------ //

    // ------------------------------------------------------ CLIENT ENDPOINTS ------------------------------------------------------ //
    // REFERENCES - REFERENCE_CATEGORIES
    case ENDPOINTS.CLIENT_REFERENCE_CATEGORIES_REFERENCES_GET_ALL:
      return `${BASE_URLS.REFERENCES.CLIENT_REFERENCE_CATEGORIES}/${endpoint.params.id}/references`;

    // REFERENCES - REFERENCES
    case ENDPOINTS.CLIENT_REFERENCES_FIELDS_GET_VALUE:
      return `${BASE_URLS.REFERENCES.CLIENT_REFERENCES}/${endpoint.params.id}/fields/${endpoint.params.field_id}/value`;
    // ------------------------------------------------------ CLIENT ENDPOINTS ------------------------------------------------------ //

    // ------------------------------------------------------ EXTERNAL ENDPOINTS ------------------------------------------------------ //
    // REFERENCES - REFERENCE_CATEGORIES
    case ENDPOINTS.EXTERNAL_REFERENCE_CATEGORIES_REFERENCES_GET_ALL:
      return `${BASE_URLS.REFERENCES.EXTERNAL_REFERENCE_CATEGORIES}/${endpoint.params.id}/references`;

    // REFERENCES - REFERENCES
    case ENDPOINTS.EXTERNAL_REFERENCES_FIELDS_GET_VALUE:
      return `${BASE_URLS.REFERENCES.EXTERNAL_REFERENCES}/${endpoint.params.id}/fields/${endpoint.params.field_id}/value`;
    // ------------------------------------------------------ EXTERNAL ENDPOINTS ------------------------------------------------------ //

    // ------------------------------------------------------ IAM ENDPOINTS ------------------------------------------------------ //
    // REFERENCES - REFERENCE_CATEGORIES
    case ENDPOINTS.IAM_REFERENCE_CATEGORIES_REFERENCES_GET_ALL:
      return `${BASE_URLS.REFERENCES.IAM_REFERENCE_CATEGORIES}/${endpoint.params.id}/references`;

    // REFERENCES - REFERENCES
    case ENDPOINTS.IAM_REFERENCES_FIELDS_GET_VALUE:
      return `${BASE_URLS.REFERENCES.IAM_REFERENCES}/${endpoint.params.id}/fields/${endpoint.params.field_id}/value`;
    // ------------------------------------------------------ IAM ENDPOINTS ------------------------------------------------------ //

    // ------------------------------------------------------ SAAS ENDPOINTS ------------------------------------------------------ //
    case ENDPOINTS.SAAS_TENANT:
      return `${BASE_URLS.SAAS.TENANT}`;

    case ENDPOINTS.SAAS_TENANT_DELETE_ONE:
      return `${BASE_URLS.SAAS.TENANT}/${endpoint.params.id}`;

    case ENDPOINTS.SAAS_TENANT_FETCH_ONE:
      return `${BASE_URLS.SAAS.TENANT}/${endpoint.params.id}`;

    case ENDPOINTS.SAAS_TENANT_STORAGE_QUOTA:
      return `${BASE_URLS.SAAS.TENANT}/tenant-storage`;

    case ENDPOINTS.SAAS_TENANT_UPDATE_ONE:
      return `${BASE_URLS.SAAS.TENANT}/${endpoint.params.id}`;

    case ENDPOINTS.SAAS_TENANT_RESTORE_ONE:
      return `${BASE_URLS.SAAS.TENANT}/${endpoint.params.id}/restore`;

    case ENDPOINTS.SAAS_TENANT_BLOCK_ONE:
      return `${BASE_URLS.SAAS.TENANT}/${endpoint.params.id}/block`;

    case ENDPOINTS.SAAS_TENANT_UNBLOCK_ONE:
      return `${BASE_URLS.SAAS.TENANT}/${endpoint.params.id}/unblock`;

    case ENDPOINTS.SAAS_TENANT_CHANGE_SUBSCRIPTIONS:
      return `${BASE_URLS.SAAS.TENANT}/change-plan`;

    case ENDPOINTS.SAAS_TENANT_CANCEL_SUBSCRIPTION:
      return `${BASE_URLS.SAAS.TENANT}/cancel-plan`;

    case ENDPOINTS.SAAS_TENANT_CONFIG:
      return `${BASE_URLS.SAAS.TENANT}/config`;

    case ENDPOINTS.SAAS_TENANT_SINGLE_CONFIGURATION:
      return `${BASE_URLS.SAAS.TENANT}/configuration`;

    case ENDPOINTS.SAAS_TENANT_USER_LIMIT:
      return `${BASE_URLS.SAAS.TENANT}/${endpoint.params.id}/user-limit`;

    case ENDPOINTS.SAAS_OWN_TENANT_USER_LIMIT:
      return `${BASE_URLS.SAAS.TENANT}/user-limit`;

    case ENDPOINTS.SAAS_SUBSCRIPTIONS:
    case ENDPOINTS.SAAS_SUBSCRIPTION_CREATE:
      return `${BASE_URLS.SAAS.SUBSCRIPTIONS}`;

    case ENDPOINTS.SAAS_SUBSCRIPTION_UPDATE_ONE:
    case ENDPOINTS.SAAS_SUBSCRIPTION_DELETE_ONE:
    case ENDPOINTS.SAAS_SUBSCRIPTION_GET_ONE:
      return `${BASE_URLS.SAAS.SUBSCRIPTIONS}/${endpoint.params.id}`;

    case ENDPOINTS.SAAS_SUBSCRIPTION_RESTORE_ONE:
      return `${BASE_URLS.SAAS.SUBSCRIPTIONS}/${endpoint.params.id}/restore`;

    case ENDPOINTS.SAAS_SUBSCRIPTION_ACTIVATE_ONE:
      return `${BASE_URLS.SAAS.SUBSCRIPTIONS}/${endpoint.params.id}/activate`;

    case ENDPOINTS.SAAS_SUBSCRIPTION_DEACTIVATE_ONE:
      return `${BASE_URLS.SAAS.SUBSCRIPTIONS}/${endpoint.params.id}/deactivate`;

    case ENDPOINTS.SAAS_SUBSCRIPTION_DEFAULT_ONE:
      return `${BASE_URLS.SAAS.SUBSCRIPTIONS}/${endpoint.params.id}/default`;

    case ENDPOINTS.SAAS_INVOICES:
      return `${BASE_URLS.SAAS.INVOICES}`;

    case ENDPOINTS.SAAS_INVOICES_SINGLE:
      return `${BASE_URLS.SAAS.INVOICES}/${endpoint.params.id}`;

    case ENDPOINTS.SAAS_PAYMENTS:
      return `${BASE_URLS.SAAS.PAYMENT}`;

    case ENDPOINTS.SAAS_PAYMENTS_MANUAL:
      return `${BASE_URLS.SAAS.PAYMENT}/manual-payment`;

    case ENDPOINTS.SAAS_PAYMENTS_INVOICE:
      return `${BASE_URLS.SAAS.PAYMENT}/${endpoint.params.id}/find-payments`;

    case ENDPOINTS.SAAS_INVOICES_PAYMENTS:
      return `${BASE_URLS.SAAS.INVOICES}/${endpoint.params.id}/find-payments`;

    case ENDPOINTS.SAAS_ADMIN_SETUP:
      return `${BASE_URLS.SAAS_SETUP.ADMIN_CONFIG}`;

    case ENDPOINTS.SAAS_OPEN_PAYMENTS:
      return `${BASE_URLS.SAAS.PAYMENT}/${endpoint.params.tenantId}/open-payments`;

    default:
      return '';

    // ------------------------------------------------------ IAM ENDPOINTS ------------------------------------------------------ //

    // ------------------------------------------------------ BMS ENDPOINTS ------------------------------------------------------ //

    // Core
    case ENDPOINTS.AGENTS_CONNECTIONS:
      return `${BASE_URLS.IDENTITIES}/agents-connections`;
    case ENDPOINTS.GET_ADMINS:
      return `${BASE_URLS.IDENTITIES}/admins`;

    // COMMISSIONS
    case ENDPOINTS.COMMISSIONS_GET_ALL:
      return `${BASE_URLS.COMMISSIONS}`;
    case ENDPOINTS.COMMISSIONS_GET_ONE:
      return `${BASE_URLS.COMMISSIONS}/${endpoint.params.id}`;

    case ENDPOINTS.COMMISSION_CODES_UPDATE_DELETE:
      return `${BASE_URLS.COMMISSION_CODE}/${endpoint.params.id}/delete`;

    // AGENT COMMISSION CODES
    case ENDPOINTS.AGENT_COMMISSION_CODES_GET_ALL:
    case ENDPOINTS.AGENT_COMMISSION_CODES_SETUP:
      return `${BASE_URLS.AGENT_COMMISSION_CODES}`;
    case ENDPOINTS.AGENT_COMMISSION_CODES_GET_ONE:
    case ENDPOINTS.AGENT_COMMISSION_CODES_UPDATE:
    case ENDPOINTS.AGENT_COMMISSION_CODES_DELETE:
      return `${BASE_URLS.AGENT_COMMISSION_CODES}/${endpoint.params.id}`;

    // PRODUCT
    case ENDPOINTS.CREATE_NEW_PRODUCT:
    case ENDPOINTS.PRODUCTS_GET_ALL:
      return BASE_URLS.PRODUCTS;
    case ENDPOINTS.GET_PRODUCTS_ID_NAME:
      return `${BASE_URLS.PRODUCTS}/getProductIdAndNameAsKeyValue`;
    case ENDPOINTS.GET_PRODUCTS_KEY_VALUE_WITH_METADATA:
      return `${BASE_URLS.PRODUCTS}/getProductKeyValueWithMetadata`;
    case ENDPOINTS.PRODUCT_DELETE_ONE:
    case ENDPOINTS.PRODUCT_UPDATE_ONE:
      return `${BASE_URLS.PRODUCTS}/${endpoint.params.id}`;
    case ENDPOINTS.PRODUCT_RESTORE_ONE:
      return `${BASE_URLS.PRODUCTS}/${endpoint.params.id}/restore`;
    case ENDPOINTS.PRODUCTS_GET_PARTIAL:
      return `${BASE_URLS.PRODUCTS}/${endpoint.params.type}/individual-products`;
    case ENDPOINTS.PRODUCT_VALIDATE_DUPLICATE:
      return `${BASE_URLS.PRODUCTS}/${endpoint.params.productName}/validate-duplicate`;

    // INVOICES

    case ENDPOINTS.CREATE_NEW_INVOICE:
    case ENDPOINTS.GET_ALL_INVOICES:
      return BASE_URLS.INVOICES;
    case ENDPOINTS.GET_ISIB_MODIFIED_DATA:
      return `${BASE_URLS.INVOICES}/isib-modified-data/`;
    case ENDPOINTS.INVOICE_SEND_REQUEST:
      return `${BASE_URLS.INVOICES}/${endpoint.params.id}/send-to-customer`;
    case ENDPOINTS.INVOICES_SETTLE_PREMIUM_BULK:
      return `${BASE_URLS.INVOICES}/bulkPayment`;
    case ENDPOINTS.INVOICE_UPDATE_ONE:
    case ENDPOINTS.INVOICES_GET_ONE:
      return `${BASE_URLS.INVOICES}/${endpoint.params.id}`;
    case ENDPOINTS.GET_INVOICE_DATA_CREDIT_DEBIT_NOTES:
      return `${BASE_URLS.INVOICES}/invoice-for-credit-debit-notes`;
    case ENDPOINTS.INVOICES_SETTLE_PREMIUM:
      return `${BASE_URLS.INVOICES}/settle-premium/${endpoint.params.id}`;
    case ENDPOINTS.INVOICES_UPLOAD_RECEIPT:
      return `${BASE_URLS.INVOICES}/${endpoint.params.id}/upload-receipt`;
    // INVOICE BULK
    case ENDPOINTS.GET_DOWNLOAD_TEMPLATE:
    case ENDPOINTS.UPLOAD_PROCESS_CSV:
    case ENDPOINTS.INVOICE_BULK_UPLOAD_SAVE_ROW:
      return BASE_URLS.INVOICE_BULK;
    case ENDPOINTS.GET_ALL_INVOICE_BULK_ROWS:
      return `${BASE_URLS.INVOICE_BULK}/getAllRows`;
    case ENDPOINTS.INVOICE_BULK_UPLOAD_SAVE_DOCUMENTS:
      return `${BASE_URLS.INVOICE_BULK}/process`;
    // ISIB

    case ENDPOINTS.UPDATE_ISIB_INVOICE:
      return `${BASE_URLS.INVOICE_ISIB}/${endpoint.params.id}`;
    case ENDPOINTS.UPDATE_ISIB_INVOICE_DATA:
      return `${BASE_URLS.INVOICE_ISIB}/update-invoice-data/${endpoint.params.id}`;
    case ENDPOINTS.UPDATE_ISIB_DATA_INVOICE_DIALOG_RESULT:
      return `${BASE_URLS.INVOICE_ISIB}/update-invoice-dialog-result/${endpoint.params.id}`;
    case ENDPOINTS.ISIB_INVOICE_RE_REQUEST:
      return `${BASE_URLS.INVOICE_ISIB}/re-request/${endpoint.params.id}`;
    case ENDPOINTS.RECREATE_ISIB_INVOICE:
      return `${BASE_URLS.INVOICE_ISIB}/recreate/${endpoint.params.id}`;

    // RECEIPTS

    case ENDPOINTS.RECEIPT_CREATION:
    case ENDPOINTS.GET_ALL_RECEIPTS:
      return BASE_URLS.RECEIPTS;
    case ENDPOINTS.RECEIPTS_GENERATE_RECEIPT:
      return `${BASE_URLS.RECEIPTS}/${endpoint.params.id}/generate-receipt`;

    // POLICIES
    case ENDPOINTS.POLICY_GET_ONE_FOR_INVOICE:
      return `${BASE_URLS.POLICIES}/invoiceFilterKey`;
    case ENDPOINTS.CREATE_NEW_POLICY_WITHOUT_LEAD:
      return `${BASE_URLS.POLICIES}/quotation`;
    case ENDPOINTS.CREATE_NEW_POLICY:
      return `${BASE_URLS.POLICIES}/${endpoint.params.lead_id}/quotation`;
    case ENDPOINTS.LEADS_GET_POLICY_BY_LEAD_ID:
      return `${BASE_URLS.POLICIES}/${endpoint.params.lead_id}/quotationAnalysis`;
    case ENDPOINTS.GET_POLICIES_WITH_YEAR:
      return `${BASE_URLS.POLICIES}/${endpoint.params.policyNumber}/policyWithDate`;
    case ENDPOINTS.POLICIES_ADD_PRODUCT:
      return `${BASE_URLS.POLICIES}/${endpoint.params.lead_id}/add-product`;
    case ENDPOINTS.GET_POLICIES_BY_LEAD_ID:
      return `${BASE_URLS.POLICIES}/${endpoint.params.lead_id}/leads`;
    case ENDPOINTS.UPLOAD_QUOTATION:
      return `${BASE_URLS.POLICIES}/${endpoint.params.id}/upload-quotation`;
    case ENDPOINTS.ACCEPT_A_QUOTATION:
      return `${BASE_URLS.POLICIES}/${endpoint.params.id}/accept-quotation`;
    case ENDPOINTS.QUOTATION_GET_PROPOSAL_DOCS:
      return `${BASE_URLS.POLICIES}/${endpoint.params.id}/documents/${endpoint.params.type}`;
    case ENDPOINTS.QUOTATION_UPLOAD_PROPOSAL_DOCS:
      return `${BASE_URLS.POLICIES}/${endpoint.params.id}/upload-proposal`;
    case ENDPOINTS.QUOTATION_RE_UPLOAD_PROPOSAL_DOCS:
      return `${BASE_URLS.POLICIES}/${endpoint.params.id}/reupload-proposal`;
    case ENDPOINTS.POLICY_SHARE_TO_GLOBAL_PARTNER:
      return `${BASE_URLS.POLICIES}/${endpoint.params.id}/share-invoice-partner`;
    case ENDPOINTS.POLICY_RESPONSE_FROM_GLOBAL_PARTNER:
      return `${BASE_URLS.POLICIES}/${endpoint.params.id}/partner-response`;
    case ENDPOINTS.GET_DEFAULT_COMMISSION:
      return `${BASE_URLS.POLICIES}/${endpoint.params.id}/filter-default-commission`;
    case ENDPOINTS.POLICY_GET_NOT_CLOSED:
      return `${BASE_URLS.POLICIES}/leads/not-close`;
    case ENDPOINTS.POLICIES_SEND_REQUEST:
      return `${BASE_URLS.POLICIES}/${endpoint.params.id}/send-request`;
    case ENDPOINTS.POLICY_GET_SINGLE:
      return `${BASE_URLS.POLICIES}/${endpoint.params.id}`;
    case ENDPOINTS.POLICIES_EMAIL_SUBJECT_PREVIEW:
      return `${BASE_URLS.POLICIES}/${endpoint.params.id}/email-subject-preview`;
    case ENDPOINTS.POLICIES_SHARE_QUOTATION_CUSTOMER:
      return `${BASE_URLS.POLICIES}/${endpoint.params.id}/share-quotation`;
    case ENDPOINTS.POLICIES_SHARE_PROPOSAL_CUSTOMER:
      return `${BASE_URLS.POLICIES}/${endpoint.params.id}/share-proposal`;
    case ENDPOINTS.POLICIES_RE_REQUEST_QUOTATION:
      return `${BASE_URLS.POLICIES}/${endpoint.params.id}/re-request`;
    case ENDPOINTS.POLICIES_RENEWAL_POLICIES:
      return `${BASE_URLS.POLICIES}/${endpoint.params.id}/renewal`;
    case ENDPOINTS.POLICIES_SHARE_POLICY_SCHEDULE_CUSTOMER:
      return `${BASE_URLS.POLICIES}/${endpoint.params.id}/share-policy-schedule`;
    case ENDPOINTS.POLICIES_RENEWAL_DOCUMENTS_UPLOAD:
      return `${BASE_URLS.POLICIES}/${endpoint.params.id}/renewal-upload-documents`;
    case ENDPOINTS.POLICIES_RENEWAL_DOCUMENTS_SHARE:
      return `${BASE_URLS.POLICIES}/${endpoint.params.id}/share-renewal`;
    case ENDPOINTS.POLICIES_RENEWAL_INVOICE_UPLOAD:
      return `${BASE_URLS.POLICIES}/${endpoint.params.id}/renewal-upload-invoice`;
    case ENDPOINTS.POLICIES_RENEWAL_INVOICE_REUPLOAD:
      return `${BASE_URLS.POLICIES}/${endpoint.params.id}/renewal-reupload-invoice/${endpoint.params.documentId}`;
    case ENDPOINTS.POLICIES_RENEWAL_INVOICE_SHARE:
      return `${BASE_URLS.POLICIES}/${endpoint.params.id}/renewal-share-invoice/${endpoint.params.documentId}`;
    case ENDPOINTS.POLICIES_RENEWAL_INVOICE_ACCEPT:
      return `${BASE_URLS.POLICIES}/${endpoint.params.id}/renewal-accept-invoice/${endpoint.params.documentId}`;
    case ENDPOINTS.POLICIES_RENEWAL_INVOICE_RENEWAL:
      return `${BASE_URLS.POLICIES}/${endpoint.params.id}/renewal-add-invoice`;
    case ENDPOINTS.GET_POLICIES_BY_POLICY_NUMBER:
      return `${BASE_URLS.POLICIES}/policy/${endpoint.params.policyNumber}`;
    case ENDPOINTS.GET_POLICIES_BY_POLICY_ID:
      return `${BASE_URLS.POLICIES}/policy/${endpoint.params.policyId}`;
    case ENDPOINTS.POLICIES_RE_ASSIGNED_HANDLER:
      return `${BASE_URLS.POLICIES}/${endpoint.params.id}/account-handler`;

    //POLICIES MEMBER LIST
    case ENDPOINTS.POLICIES_MEMBER_LIST_GET_ALL:
      return `${BASE_URLS.POLICIES_MEMBER_LIST}`;
    case ENDPOINTS.POLICIES_MEMBER_LIST_GET_ONE:
      return `${BASE_URLS.POLICIES_MEMBER_LIST}/${endpoint.params.id}`;
    case ENDPOINTS.POLICIES_MEMBER_LIST_CREATE_ONE:
      return `${BASE_URLS.POLICIES_MEMBER_LIST}/${endpoint.params.policyId}`;
    case ENDPOINTS.POLICIES_MEMBER_LIST_UPDATE_ONE:
      return `${BASE_URLS.POLICIES_MEMBER_LIST}/${endpoint.params.id}`;
    case ENDPOINTS.POLICIES_MEMBER_LIST_DELETE_ONE:
      return `${BASE_URLS.POLICIES_MEMBER_LIST}/${endpoint.params.id}`;
    case ENDPOINTS.POLICIES_MEMBER_LIST_RESTORE_ONE:
      return `${BASE_URLS.POLICIES_MEMBER_LIST}/${endpoint.params.id}/restore`;
    case ENDPOINTS.POLICIES_MEMBER_LIST_BULK_DOWNLOAD:
      return `${BASE_URLS.POLICIES_MEMBER_LIST}/bulk-temp-download`;
    case ENDPOINTS.POLICIES_MEMBER_LIST_BULK_SUBMIT:
      return `${BASE_URLS.POLICIES_MEMBER_LIST}/${endpoint.params.uuid}/bulk-submit`;
    case ENDPOINTS.POLICIES_MEMBER_LIST_BULK_UPLOAD:
      return `${BASE_URLS.POLICIES_MEMBER_LIST}/${endpoint.params.policyId}/bulk-upload`;
    case ENDPOINTS.POLICIES_MEMBER_LIST_BULK_DELETE:
      return `${BASE_URLS.POLICIES_MEMBER_LIST}/bulk-temp-delete`;
    case ENDPOINTS.POLICIES_MEMBER_LIST_BULK_DELETE_SUBMIT:
      return `${BASE_URLS.POLICIES_MEMBER_LIST}/${endpoint.params.policyId}/bulk-delete`;
    case ENDPOINTS.POLICIES_MEMBER_LIST_DOWNLOAD:
      return `${BASE_URLS.POLICIES_MEMBER_LIST}/${endpoint.params.policyId}/download-members`;
    case ENDPOINTS.POLICIES_MEMBER_LIST_CUSTOMER_POLICIES:
      return `${BASE_URLS.POLICIES_MEMBER_LIST}/${endpoint.params.policyId}/customer-policies`;
    case ENDPOINTS.POLICIES_MEMBER_LIST_IMPORTABLE_MEMBERS:
      return `${BASE_URLS.POLICIES_MEMBER_LIST}/${endpoint.params.policyId}/importable-members`;
    case ENDPOINTS.POLICIES_MEMBER_LIST_IMPORT_MEMBERS:
      return `${BASE_URLS.POLICIES_MEMBER_LIST}/${endpoint.params.policyId}/import-members`;

    // INSURER
    case ENDPOINTS.INSURERS_SINGLE:
      return `${BASE_URLS.INSURERS}/${endpoint.params.id}`;
    case ENDPOINTS.INSURERS_SINGLE_RESTORE:
      return `${BASE_URLS.INSURERS}/${endpoint.params.id}/restore`;
    case ENDPOINTS.INSURERS_MULTI:
      return `${BASE_URLS.INSURERS}`;
    case ENDPOINTS.GET_INSURER_ID_NAME:
      return `${BASE_URLS.INSURERS}/getInsurerIdAndNameAsKeyValue`;
    case ENDPOINTS.GET_INSURANCE_CONTACT_DATA:
      return `${BASE_URLS.INSURERS}/${endpoint.params.insurerId}/products/${endpoint.params.productId}/contacts`;
    case ENDPOINTS.INSURERS_MULTI_WITH_PRODUCT:
      return `${BASE_URLS.INSURERS}/withProducts`;
    case ENDPOINTS.GET_INSURER_LIST:
      return `${BASE_URLS.INSURERS}/getList/${endpoint.params.id}`;
    case ENDPOINTS.INSURERS_GET_PROPOSAL_DOC:
      return `${BASE_URLS.INSURERS}/${endpoint.params.insurerId}/${endpoint.params.productId}/proposalDoc`;
    case ENDPOINTS.INSURERS_ACTIVE:
      return `${BASE_URLS.INSURERS}/${endpoint.params.id}/active`;
    case ENDPOINTS.INSURERS_DE_ACTIVE:
      return `${BASE_URLS.INSURERS}/${endpoint.params.id}/de-active`;

    // LEADS
    case ENDPOINTS.LEADS_ADD_NEW:
    case ENDPOINTS.LEADS_GET_ALL:
      return BASE_URLS.LEADS;
    case ENDPOINTS.LEADS_GET_ALL_ACCOUNT_HANDLERS:
      return `${BASE_URLS.LEADS}/accountHandlers`;
    case ENDPOINTS.LEADS_SEND_EMAILS:
      return `${BASE_URLS.LEADS}/send-emails`;

    case ENDPOINTS.LEADS_UPDATE_ONE:
      return `${BASE_URLS.LEADS}/${endpoint.params.id}/update`;
    case ENDPOINTS.LEADS_GET_ONE_FOR_INVOICE:
      return `${BASE_URLS.LEADS}/${endpoint.params.id}/invoice`;

    case ENDPOINTS.LEADS_GET_ONE:
      return `${BASE_URLS.LEADS}/${endpoint.params.id}`;

    case ENDPOINTS.LEADS_PROSPECT_UPDATE:
      return `${BASE_URLS.LEADS}/${endpoint.params.id}/prospect`;

    case ENDPOINTS.LEADS_CREATE_NEW:
      return `${BASE_URLS.LEADS}/create-new-lead`;
    case ENDPOINTS.LEADS_GET_QUOTATIONS_DOCUMENTS:
      return `${BASE_URLS.LEADS}/${endpoint.params.id}/get-quotations`;
    case ENDPOINTS.GET_ANALYSIS_REPORT:
      return `${BASE_URLS.LEADS}/get-analysis-report`;
    case ENDPOINTS.SHARE_ANALYSIS_REPORT:
      return `${BASE_URLS.LEADS}/${endpoint.params.file_id}/share-analysis-report`;
    case ENDPOINTS.UPLOAD_ANALYSIS_REPORT:
      return `${BASE_URLS.LEADS}/${endpoint.params.id}/upload-analysis-report`;
    case ENDPOINTS.GENERATE_ANALYSIS_REPORT:
      return `${BASE_URLS.LEADS}/${endpoint.params.id}/generate-analysis-report`;
    case ENDPOINTS.REGENERATE_ANALYSIS_REPORT:
      return `${BASE_URLS.LEADS}/${endpoint.params.id}/regenerate-analysis-report`;

    // EXTERNAL USERS
    case ENDPOINTS.CREATE_EXTERNAL_USERS:
    case ENDPOINTS.GET_ALL_EXTERNAL_USERS:
      return BASE_URLS.EXTERNAL_USERS;
    case ENDPOINTS.GET_EXTERNAL_USERS:
      return `${BASE_URLS.EXTERNAL_USERS}/data/${endpoint.params.id}`;

    // CREDIT DEBIT NOTES
    case ENDPOINTS.CREATE_CREDIT_DEBIT_NOTE:
    case ENDPOINTS.GET_ALL_CREDIT_DEBIT_NOTES:
      return BASE_URLS.CREDIT_DEBIT_NOTES;

    // QUOTATION
    case ENDPOINTS.CREATE_NEW_QUOTATION:
    case ENDPOINTS.QUOTATIONS_GET_ALL:
      return BASE_URLS.QUOTATIONS;

    // REPORTS
    case ENDPOINTS.REPORTS_CREATE_CONFIGURE:
    case ENDPOINTS.REPORTS_GET_CONFIGURE:
      return BASE_URLS.REPORTS;
    case ENDPOINTS.REPORTS_GET_INVOICES:
      return `${BASE_URLS.REPORTS}/renewal-invoice`;
    case ENDPOINTS.REPORTS_GET_INVOICES_DATA:
      return `${BASE_URLS.REPORTS}/renewal-data`;
    case ENDPOINTS.REPORTS_GET_CLAIMS:
      return `${BASE_URLS.REPORTS}/claim-report`;
    case ENDPOINTS.REPORTS_GET_CLAIMS_DATA:
      return `${BASE_URLS.REPORTS}/claims-data`;
    case ENDPOINTS.REPORTS_GET_PAYMENTS_REPORT:
      return `${BASE_URLS.REPORTS}/payments-report`;
    case ENDPOINTS.REPORTS_GET_IRCSL_DATA:
      return `${BASE_URLS.REPORTS}/ircsl-data`;
    case ENDPOINTS.REPORTS_GET_IRCSL_REPORT:
      return `${BASE_URLS.REPORTS}/ircsl-report`;
    case ENDPOINTS.REPORTS_GET_GWP_DATA:
      return `${BASE_URLS.REPORTS}/gwp-data`;
    case ENDPOINTS.REPORTS_GET_GWP:
      return `${BASE_URLS.REPORTS}/gwp-report`;
    case ENDPOINTS.REPORTS_GET_FULL_REPORT:
      return `${BASE_URLS.REPORTS}/full-report`;
    case ENDPOINTS.REPORTS_GET_COMMISSIONS_DATA:
      return `${BASE_URLS.REPORTS}/commissions-data`;
    case ENDPOINTS.REPORTS_GET_COMMISSIONS:
      return `${BASE_URLS.REPORTS}/commissions-report`;

    // CLAIMS
    case ENDPOINTS.CREATE_NEW_CLAIM:
    case ENDPOINTS.CLAIMS_GET_ALL:
      return BASE_URLS.CLAIMS;
    case ENDPOINTS.CLAIMS_GET_CSV_DATA:
      return `${BASE_URLS.CLAIMS}/claim-csv`;
    case ENDPOINTS.CLAIM_GET_BULK_TEMPLATE:
      return `${BASE_URLS.CLAIMS}/bulk-temp-download`;
    case ENDPOINTS.STREAMLINED_CLAIM_GET_BULK_TEMPLATE:
      return `${BASE_URLS.CLAIMS}/streamlined-bulk-temp-download`;
    case ENDPOINTS.CLAIM_DOWNLOAD_TEMPLATE_WITH_DATA:
      return `${BASE_URLS.CLAIMS}/download-template-with-data`;
    case ENDPOINTS.CLAIM_BULK_UPDATE_VALIDATE:
      return `${BASE_URLS.CLAIMS}/bulk-update-validate`;
    case ENDPOINTS.CLAIM_BULK_UPDATE_EXECUTE:
      return `${BASE_URLS.CLAIMS}/bulk-update-execute`;
    case ENDPOINTS.CLAIM_BULK_UPLOAD:
      return `${BASE_URLS.CLAIMS}/bulk-upload`;
    case ENDPOINTS.STREAMLINED_CLAIM_BULK_UPLOAD:
      return `${BASE_URLS.CLAIMS}/streamlined-bulk-upload`;
    case ENDPOINTS.CLAIM_BULK_LIST_GET_ALL:
      return `${BASE_URLS.CLAIMS}/claim-bulk-list`;
    case ENDPOINTS.CLAIMS_SEND_REQUEST:
      return `${BASE_URLS.CLAIMS}/${endpoint.params.id}/send-request-additional-document`;
    case ENDPOINTS.CLAIMS_GET_PENDING_COUNT:
      return `${BASE_URLS.CLAIMS}/pending-count/${endpoint.params.nic}`;
    case ENDPOINTS.CLAIM_GET_ONE:
    case ENDPOINTS.CLAIM_UPDATE_ONE:
      return `${BASE_URLS.CLAIMS}/${endpoint.params.id}`;
    case ENDPOINTS.CLAIMS_REMARKS:
      return `${BASE_URLS.CLAIMS}/${endpoint.params.id}/remarks`;
    case ENDPOINTS.CLAIM_BULK_DELETE_ONE:
      return `${BASE_URLS.CLAIMS}/${endpoint.params.id}/delete-claim-bulk`;
    case ENDPOINTS.CLAIM_GET_BATCH_NUMBER:
      return `${BASE_URLS.CLAIMS}/${endpoint.params.id}/batch-number`;
    case ENDPOINTS.CLAIMS_ADDITIONAL_DOCUMENT:
      return `${BASE_URLS.CLAIMS}/${endpoint.params.id}/additional-document`;
    case ENDPOINTS.CLAIM_SHARE_TO_CUSTOMER:
      return `${BASE_URLS.CLAIMS}/share-document`;
    case ENDPOINTS.CUSTOMERS_GET_PENDING_CLAIMS:
      return `${BASE_URLS.CLAIMS}/pending-count/${endpoint.params.policyNo}`;
    case ENDPOINTS.CLAIM_SEND_REQUEST:
      return `${BASE_URLS.CLAIMS}/${endpoint.params.id}/send-request`;
    case ENDPOINTS.CLAIM_ADD_INTIMATION:
      return `${BASE_URLS.CLAIMS}/${endpoint.params.id}/add-intimation`;
    case ENDPOINTS.CLAIM_ADD_RESPONSE:
      return `${BASE_URLS.CLAIMS}/${endpoint.params.id}/response`;

    //CUSTOMERS
    case ENDPOINTS.CUSTOMERS_GET_ALL:
      return BASE_URLS.CUSTOMERS;
    case ENDPOINTS.CUSTOMERS_GET_BY_NIC:
      return `${BASE_URLS.CUSTOMERS}/nic`;
    case ENDPOINTS.CUSTOMERS_UPDATE_NIC_PASSPORT:
      return `${BASE_URLS.CUSTOMERS}/${endpoint.params.id}/update-nic-passport`;
    case ENDPOINTS.CUSTOMERS_GET_ONE:
      return `${BASE_URLS.CUSTOMERS}/${endpoint.params.id}`;
    case ENDPOINTS.CUSTOMERS_GET_TEMP_POLICY:
      return `${BASE_URLS.CUSTOMERS}/${endpoint.params.id}/temp-policy`;
    case ENDPOINTS.CUSTOMERS_CREATE_CUSTOMER:
      return `${BASE_URLS.CUSTOMERS}`;

    case ENDPOINTS.GET_DOWNLOAD_TEMPLATE_CUSTOMERS:
    case ENDPOINTS.UPLOAD_PROCESS_CSV_CUSTOMERS:
      return `${BASE_URLS.CUSTOMERS_BULK}`;
    case ENDPOINTS.GET_ALL_CUSTOMER_BULK_ROWS:
      return `${BASE_URLS.CUSTOMERS_BULK}/getAllRows`;
    case ENDPOINTS.CUSTOMER_BULK_UPLOAD_SAVE_DOCUMENTS:
      return `${BASE_URLS.CUSTOMERS_BULK}/process`;

    //ADDITIONAL_STORAGE_USERS
    case ENDPOINTS.GET_ALL_ADDITIONAL_STORAGE_USERS:
      return BASE_URLS.ADDITIONAL_STORAGE;

    //RECONCILIATION
    case ENDPOINTS.RECONCILIATION:
      return `${BASE_URLS.RECONCILE}/process`;
    case ENDPOINTS.CREATE_RECONCILIATION:
      return `${BASE_URLS.RECONCILE}/create`;
    case ENDPOINTS.FILTER_RECONCILIATION:
      return `${BASE_URLS.RECONCILE}`;
    case ENDPOINTS.GET_RECONCILIATION_BY_ID:
      return `${BASE_URLS.RECONCILE}/${endpoint.params.id}`;
  }
};

export const generateURL = (endpoint: EndpointParams): string => {
  const path = getURLPath(endpoint);
  const apiVersion = endpoint.version ? `/v${endpoint.version}` : '';

  return `/api${apiVersion}${path}`;
};
